import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Flag, Package, Mail, Clock, CheckCircle2 } from "lucide-react";

const CATEGORY_BADGE = {
  damaged: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/20",
  missing: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20",
  other: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/20",
};

const STATUS_BADGE = {
  open: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/20",
  in_progress: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20",
  resolved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
};

const FILTERS = ["all", "open", "in_progress", "resolved"];

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function AdminComplaints() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/complaints");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/complaints/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const shown = items.filter((c) => filter === "all" || c.status === filter);
  const openCount = items.filter((c) => c.status !== "resolved").length;

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-complaints">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Support</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
            <Flag className="w-6 h-6 text-paw-accent" strokeWidth={1.5} /> Complaints
          </h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-1">{openCount} open, {items.length} total. Filed by phone through Ava.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="complaints-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 h-9 rounded-lg font-manrope text-sm border transition-colors ${filter === f ? "bg-paw-accent/15 text-paw-accent border-paw-accent/30" : "bg-black/[0.03] dark:bg-white/5 text-paw-text-secondary dark:text-white/60 border-paw-border dark:border-white/10 hover:text-paw-text dark:hover:text-white"}`}>
            {f === "all" ? "All" : f.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 text-paw-text-muted dark:text-white/30 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-24 font-manrope text-paw-text-muted dark:text-white/40">No complaints{filter === "all" ? " yet" : ` marked ${filter.replace("_", " ")}`}.</div>
      ) : (
        <div className="space-y-3">
          {shown.map((c) => (
            <div key={c.complaint_id} className="rounded-xl bg-black/[0.02] dark:bg-white/[0.03] border border-paw-border dark:border-white/10 p-4 sm:p-5" data-testid="complaint-row">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-mono text-sm text-paw-text dark:text-white/80">{c.ref}</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${CATEGORY_BADGE[c.category] || CATEGORY_BADGE.other}`}>{c.category}</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${STATUS_BADGE[c.status] || STATUS_BADGE.open}`}>{(c.status || "open").replace("_", " ")}</span>
                <span className="ml-auto flex items-center gap-1 text-xs text-paw-text-muted dark:text-white/40"><Clock className="w-3.5 h-3.5" /> {fmtDate(c.created_at)}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm font-manrope">
                <span className="flex items-center gap-2 text-paw-text-secondary dark:text-white/70"><Package className="w-4 h-4 text-paw-text-muted dark:text-white/40" /> {c.order_number || "No order matched"}</span>
                <span className="flex items-center gap-2 text-paw-text-secondary dark:text-white/70"><Mail className="w-4 h-4 text-paw-text-muted dark:text-white/40" /> {c.customer_email || "unknown"}</span>
              </div>
              {c.details ? <p className="mt-2 text-sm text-paw-text-secondary dark:text-white/60 font-manrope leading-relaxed">{c.details}</p> : null}
              {c.admin_note ? <p className="mt-2 text-xs text-paw-text-muted dark:text-white/40 font-manrope italic">Note: {c.admin_note}</p> : null}
              <div className="flex gap-2 mt-3">
                {c.status !== "in_progress" && c.status !== "resolved" && (
                  <button disabled={busy === c.complaint_id} onClick={() => setStatus(c.complaint_id, "in_progress")} className="px-3 h-8 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/20 font-manrope text-xs transition-colors disabled:opacity-60">In progress</button>
                )}
                {c.status !== "resolved" && (
                  <button disabled={busy === c.complaint_id} onClick={() => setStatus(c.complaint_id, "resolved")} className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 font-manrope text-xs transition-colors disabled:opacity-60"><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>
                )}
                {c.status === "resolved" && (
                  <button disabled={busy === c.complaint_id} onClick={() => setStatus(c.complaint_id, "open")} className="px-3 h-8 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 text-paw-text-secondary dark:text-white/60 border border-paw-border dark:border-white/10 font-manrope text-xs transition-colors disabled:opacity-60">Reopen</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
